import type { Agreements } from '../filterValues';
import {
  SEARCH_PARAMETERS_SCHEMA,
  SearchParameterFieldKind,
  type SearchParameterValues,
} from '../schema';
import type { SearchParameterSchemaRow } from '../schema/rows';

type SerializeStrategy = (value: unknown) => string | undefined;

export class SearchParameterSerializer {
  readonly #schema: readonly SearchParameterSchemaRow[];

  constructor(
    schema: readonly SearchParameterSchemaRow[] = SEARCH_PARAMETERS_SCHEMA
  ) {
    this.#schema = schema;
  }

  // One search, with no previous to compare against — so a value that exists
  // only to defeat a backend default cannot be manufactured here. Those are the
  // url's, and searchQueryAdapter states them.
  serialize = (values: SearchParameterValues): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const field of this.#schema) {
      const value = values[field.name as keyof SearchParameterValues];
      const serialized =
        this.#strategies[field.kind](value) ?? this.#preservedDefault(field);

      if (serialized && !this.#shouldDropValue(field, serialized)) {
        out[field.name] = serialized;
      }
    }
    return out;
  };

  // A preserved default is the one value a search can carry without stating it.
  // The values hold only what was stated, so the schema supplies it here rather
  // than the search carrying it around just to be serialized.
  #preservedDefault = (field: SearchParameterSchemaRow): string | undefined =>
    'preserveDefault' in field && field.preserveDefault
      ? String(field.defaultValue)
      : undefined;

  // For SEO reasons we drop most default values from the URL. The rows that
  // opt out say so themselves, via `preserveDefault` — see ../schema/rows.
  #shouldDropValue = (
    field: SearchParameterSchemaRow,
    serializedValue: string
  ): boolean =>
    'defaultValue' in field &&
    serializedValue === String(field.defaultValue) &&
    !('preserveDefault' in field && field.preserveDefault);

  #serializeString: SerializeStrategy = (value) =>
    (value as string | undefined) || undefined;

  // Both array kinds serialize the same way — only deserialization needs to
  // tell them apart, to validate enum members.
  #serializeArray: SerializeStrategy = (value) => {
    const arr = value as readonly string[] | undefined;
    return arr?.length ? arr.join(',') : undefined;
  };

  #serializeAgreements: SerializeStrategy = (value) => {
    const agreements = value as Agreements | undefined;
    if (!agreements) return undefined;

    const parts = [
      agreements.premiumaccess,
      agreements.easyaccess,
      agreements.editorialsubscription,
      agreements.royaltyfreesubscription,
    ].filter(Boolean);

    return parts.length ? parts.join(',') : undefined;
  };

  #serializeNumber: SerializeStrategy = (value) => {
    const num = value as number | undefined;
    return num !== undefined ? String(num) : undefined;
  };

  readonly #strategies: Record<SearchParameterFieldKind, SerializeStrategy> = {
    [SearchParameterFieldKind.STRING]: this.#serializeString,
    [SearchParameterFieldKind.ENUM]: this.#serializeString,
    [SearchParameterFieldKind.ENUM_ARRAY]: this.#serializeArray,
    [SearchParameterFieldKind.STRING_ARRAY]: this.#serializeArray,
    [SearchParameterFieldKind.AGREEMENTS]: this.#serializeAgreements,
    [SearchParameterFieldKind.NUMBER]: this.#serializeNumber,
  };
}
